import React, { Component, createRef } from "react";
import { StyleSheet, View, Text, TouchableOpacity, ActivityIndicator, Platform } from "react-native";
import { connect } from "react-redux";
import colors from "../../constants/colors";
import { isMobileWeb } from "../../utils/mobileWeb";
import { getItemsByLocation } from "../../actions/records";
import {
  getComponentByanotherComponentId,
  getComponentSubmit,
  getLocationId,
  getRequiredAmount,
  getTotalSelected,
  isDefaultLocation,
  simpleAlert,
  validateDecimals,
} from "../../utils";
import ItemsByLocationModal from "../ItemsByLocationModal";
import { resolveSubmissionLocationValue, normalizeLocationCompareText, resolveLocationForItemsFetch, buildGetItemsByLocationPayload } from "../../utils/locationScan";

const INVENTORY_CATEGORIES = [
  { key: "finishedGoods", label: "Finished Goods" },
  { key: "workInProgress", label: "Work In Progress" },
  { key: "rawMaterials", label: "Raw Materials" },
  { key: "packagingMaterials", label: "Packaging Materials" },
];

export { INVENTORY_CATEGORIES };

const SECTION_TYPE_KEYS = {
  finishedGoods: "finishedGoods",
  workInProgress: "workInProgress",
  rawMaterials: "rawMaterials",
  packagingMaterials: "packagingMaterials",
  ingredients: "rawMaterials",
};

const ALL_INVENTORY_SECTION = "itemsByLocation";

export function isAllInventorySection(sectionDefaultLabel) {
  return sectionDefaultLabel === ALL_INVENTORY_SECTION;
}

export function getSectionInventoryKey(components, section_id) {
  if (!components || section_id == null) return null;
  var section = components.find((c) => c.type === "section" && c.section_id == section_id);
  if (!section?.defaultLabel) return null;
  if (isAllInventorySection(section.defaultLabel)) return null;
  return SECTION_TYPE_KEYS[section.defaultLabel] || null;
}

export function getSectionDefaultLabel(components, section_id) {
  if (!components || section_id == null) return null;
  var section = components.find((c) => c.type === "section" && c.section_id == section_id);
  return section?.defaultLabel || null;
}

function unwrapLocationItemsResponse(apiResponse) {
  if (!apiResponse || typeof apiResponse !== "object") return {};
  if (
    apiResponse.finishedGoods ||
    apiResponse.workInProgress ||
    apiResponse.rawMaterials ||
    apiResponse.packagingMaterials
  ) {
    return apiResponse;
  }
  if (apiResponse.data && typeof apiResponse.data === "object") {
    return unwrapLocationItemsResponse(apiResponse.data);
  }
  return apiResponse;
}

function getProductName(product, batch) {
  return batch?.item || product?.item || product?.itemName || product?.namePackaging || product?.name || "";
}

function getLotGroups(product) {
  if (Array.isArray(product?.items) && product.items.length) {
    return product.items;
  }
  if (product?.historial?.length) {
    return [{ lot: product.lot || product?.lotCode || "", historial: product.historial }];
  }
  if (product?.lot != null || product?.lotCode != null || product?.amount != null) {
    return [product];
  }
  return [];
}

function normalizeRecordId(id) {
  if (id == null) return null;
  if (typeof id === "object" && id.$oid) return String(id.$oid);
  return String(id);
}

function normalizeHistorialBatches(product, lotGroup, defaultUnits) {
  var historial = lotGroup?.historial;
  if (Array.isArray(historial) && historial.length) {
    return historial.map((batch) => ({
      ...batch,
      lot: batch?.lot != null ? batch.lot : (lotGroup?.lot != null ? lotGroup.lot : product?.lot || product?.lotCode || ""),
      item: batch?.item || lotGroup?.item || getProductName(product),
      units: batch?.units || lotGroup?.units || defaultUnits,
      production_date: batch?.production_date || lotGroup?.production_date || product?.production_date || product?.date || "",
      productCode: batch?.productCode || product?.productCode || "",
    }));
  }
  if (lotGroup?.amount != null) {
    return [{
      amount: lotGroup.amount,
      units: lotGroup.units || defaultUnits,
      production_date: lotGroup.production_date || product?.production_date || product?.date || "",
      location: lotGroup.location,
      completeLocation: lotGroup.completeLocation,
      lot: lotGroup.lot != null ? lotGroup.lot : (product?.lot || product?.lotCode || ""),
      item: lotGroup.item || getProductName(product),
      _id: lotGroup._id,
      productCode: lotGroup.productCode || product?.productCode || "",
    }];
  }
  return [];
}

/**
 * Flatten getItemsByLocation into one picker row per historial batch.
 * When the section defaultLabel is itemsByLocation, all inventory types are included.
 */
export function formatLocationItemsResponse(apiResponse, sectionDefaultLabel) {
  var data = unwrapLocationItemsResponse(apiResponse);
  var options = [];
  var seenBatchIds = {};

  var categoryKey = null;
  if (sectionDefaultLabel && !isAllInventorySection(sectionDefaultLabel)) {
    categoryKey = SECTION_TYPE_KEYS[sectionDefaultLabel] || sectionDefaultLabel;
    if (!INVENTORY_CATEGORIES.some(({ key }) => key === categoryKey)) {
      return options;
    }
  }

  var categories = categoryKey
    ? INVENTORY_CATEGORIES.filter(({ key }) => key === categoryKey)
    : INVENTORY_CATEGORIES;

  categories.forEach(({ key, label }) => {
    var products = data[key];
    if (!Array.isArray(products)) return;
    products.forEach((product) => {
      var productCode = product?.productCode || "";
      var inventoryType = key;
      var inventoryTypeLabel = label;
      var units = product?.units || "";
      var lotGroups = getLotGroups(product);
      lotGroups.forEach((lotGroup, groupIdx) => {
        if (!lotGroup) return;
        var batches = normalizeHistorialBatches(product, lotGroup, units);
        batches.forEach((batch, batchIdx) => {
          var rawBatchId = normalizeRecordId(batch?._id);
          var batchId = `${key}-${rawBatchId || `${normalizeRecordId(product?._id) || "product"}-${groupIdx}-${batchIdx}`}`;
          if (seenBatchIds[batchId]) return;
          seenBatchIds[batchId] = true;
          var lot = batch?.lot != null ? String(batch.lot) : "";
          var itemName = getProductName(product, batch);
          var amount = parseFloat(batch?.amount);
          if (isNaN(amount)) amount = 0;

          options.push({
            item: itemName,
            productCode: batch?.productCode || productCode,
            inventoryType,
            inventoryTypeLabel,
            inventoryCategory: key,
            lot,
            batchId,
            historial: [batch],
            totalAmount: amount,
            units: batch?.units || units,
            items: product?.items,
            _id: product?._id,
            description: product?.description,
          });
        });
      });
    });
  });

  return options;
}

export function getLocationFromSubmission(components, submission, section_id, position, { isTransferByLocation, inventoryLocations } = {}) {
  if (!components || !submission) return null;
  var locationLabel = isTransferByLocation ? "fromLocation" : "location";
  var locationComponent = components.find((c) => c.section_id == section_id && c.defaultLabel == locationLabel);
  if (!locationComponent) return null;
  var raw = submission[locationComponent.id];
  if (!raw) return null;
  var value = (position != null && raw[position] != null) ? raw[position] : raw[0];
  if (!inventoryLocations?.length) {
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") return value[0];
    return value;
  }
  var resolved = resolveSubmissionLocationValue(value, inventoryLocations, { exactOnly: true });
  if (typeof resolved === "string") {
    resolved = resolveSubmissionLocationValue(resolved, inventoryLocations, { exactOnly: false });
  }
  return resolved;
}

function buildLocationSyncKey(location) {
  if (location == null || location === "") return null;
  if (typeof location === "string") {
    var label = normalizeLocationCompareText(location);
    return label ? `label:${label}` : null;
  }
  var id = getLocationId(location);
  if (id) return `id:${id}`;
  if (isDefaultLocation(location)) return "id:default";
  var rendered = normalizeLocationCompareText(renderLocationLabel(location));
  return rendered ? `label:${rendered}` : null;
}

function resolveLocationForFetch(location, inventoryLocations) {
  return resolveLocationForItemsFetch(location, inventoryLocations);
}

export function renderLocationLabel(location) {
  if (!location) return "";
  if (typeof location == "string") return location;
  var completeLocation = "";
  if (location?.site?.locationName) completeLocation += location.site.locationName;
  if (location?.zone?.locationName) completeLocation += "-" + location.zone.locationName;
  if (location?.aisle?.locationName) completeLocation += "-" + location.aisle.locationName;
  if (location?.rackBay?.locationName) completeLocation += "-" + location.rackBay.locationName;
  if (location?.shelfLevel?.locationName) completeLocation += "-" + location.shelfLevel.locationName;
  if (location?.bin?.locationName) completeLocation += "-" + location.bin.locationName;
  return completeLocation;
}

function formatBatchAmount(amount) {
  var num = parseFloat(amount);
  if (isNaN(num)) return 0;
  if (num % 1 !== 0 && !validateDecimals(String(num))) return Number(num.toFixed(3));
  return num;
}

/** Turn every available stock row into a selected item row (full location inventory). */
export function buildPreselectedFromOptions(options) {
  if (!Array.isArray(options) || !options.length) return [];
  return options.map((option) => {
    var selected = JSON.parse(JSON.stringify(option));
    if (selected.historial?.length) {
      selected.historial = selected.historial.map((batch) => ({
        ...batch,
        editedAmount: formatBatchAmount(batch?.editedAmount != null ? batch.editedAmount : batch?.amount),
        hide: false,
      }));
    }
    return selected;
  });
}

function getLocationCacheKey(props) {
  var location = getLocationFromSubmission(props.components, props.submission, props.section_id, props.position, {
    isTransferByLocation: props.isTransferByLocation,
    inventoryLocations: props.inventoryLocations,
  });
  return buildLocationSyncKey(location);
}

function getShortInventoryTypeLabel(row) {
  var label = row?.inventoryTypeLabel || row?.inventoryType || "";
  if (!label) return "";
  if (label === "Finished Goods") return "FG";
  if (label === "Work In Progress") return "WIP";
  if (label === "Raw Materials") return "RM";
  if (label === "Packaging Materials") return "PKG";
  return label;
}

function getItemsDisplayString(itemsSelected, showInventoryType) {
  if (!itemsSelected || typeof itemsSelected !== "object" || !itemsSelected.length) return "";
  var labels = itemsSelected
    .filter((row) => !row?.isNew || row?.item || row?.lot)
    .map((row) => {
      var name = row?.item || "";
      var lot = row?.lot || "";
      //var typePrefix = showInventoryType ? getShortInventoryTypeLabel(row) : "";
      //var base = name && lot ? `${name} (${lot})` : (name || lot);
      //if (!base) return "";
      //return typePrefix ? `${typePrefix}: ${base}` : base;
      return name;
    })
    .filter(Boolean);
  if (!labels.length) return "";
  if (labels.length === 1) return labels[0];
  return `${labels[0]} +${labels.length - 1}`;
}

class ItemsByLocation extends Component {
  inputRef = createRef();

  constructor(props) {
    super(props);
    this.state = {
      value: props.initial || [],
      showItemsByLocation: false,
      options: [],
      loading: false,
      locationLabel: "",
    };
    this._embeddedClosedUntil = 0;
    this._locationKey = null;
    this._fetchRequestId = 0;
    this._syncedItemsKey = null;
  }

  shouldAllowEmbeddedView() {
    return Date.now() >= this._embeddedClosedUntil;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.initial != this.props.initial) {
      this.setState({ value: nextProps.initial || [] });
    }
  }

  componentDidMount() {
    this.syncLocationItems(this.props);
  }

  componentDidUpdate(prevProps) {
    const fetch = this.props.locationItemsFetch;
    const prevFetch = prevProps.locationItemsFetch;
    if (
      fetch
      && fetch !== prevFetch
      && fetch.section_id == this.props.section_id
      && fetch.position == this.props.position
      && fetch.location
    ) {
      const key = buildLocationSyncKey(fetch.location);
      this._locationKey = key;
      this.fetchItemsAtLocation(fetch.location, { preselectAll: true });
      return;
    }
    if (
      prevProps.inventoryLocations !== this.props.inventoryLocations
      && this.props.inventoryLocations?.length
      && fetch
      && fetch.section_id == this.props.section_id
      && fetch.position == this.props.position
      && fetch.location
      && !this.state.options.length
      && !this.state.loading
    ) {
      this.fetchItemsAtLocation(fetch.location, { preselectAll: true });
      return;
    }
    if (
      prevProps.submission !== this.props.submission
      || prevProps.position !== this.props.position
      || prevProps.section_id !== this.props.section_id
      || prevProps.inventoryLocations !== this.props.inventoryLocations
    ) {
      this.syncLocationItems(this.props);
    }
  }

  componentWillUnmount() {
    const { onLocationItemsLoadingChange, isShipmentByLocation, isTransferByLocation } = this.props;
    if (this.state.loading && onLocationItemsLoadingChange && (isShipmentByLocation || isTransferByLocation)) {
      onLocationItemsLoadingChange(false);
    }
  }

  getExistingValue(props) {
    var stateValue = this.state?.value;
    if (Array.isArray(stateValue) && stateValue.length) return stateValue;
    return props.initial || [];
  }

  shouldPreselectAll(prevLocationKey, nextLocationKey, props) {
    if (!nextLocationKey) return false;
    if (prevLocationKey != null && prevLocationKey !== nextLocationKey) return true;
    if (prevLocationKey == null) {
      if (this._syncedItemsKey === nextLocationKey) return false;
      if (this.getExistingValue(props)?.length) return false;
      return true;
    }
    return false;
  }

  syncLocationItems(props) {
    var rawLocation = getLocationFromSubmission(props.components, props.submission, props.section_id, props.position, {
      isTransferByLocation: props.isTransferByLocation,
      inventoryLocations: props.inventoryLocations,
    });
    if (!rawLocation) {
      if (this._locationKey != null) {
        var prevLocationKey = this._locationKey;
        this._locationKey = null;
        this.setState({ options: [], loading: false, locationLabel: "", value: [] });
        if (prevLocationKey != null) {
          const clearId = props.id;
          const clearPosition = props.position;
          setTimeout(() => {
            if (this._locationKey == null && this.props.position === clearPosition) {
              props.onChange(clearId, []);
            }
          }, 0);
        }
      }
      return;
    }

    var locationKey = buildLocationSyncKey(rawLocation);
    if (!locationKey) return;

    var location = resolveLocationForFetch(rawLocation, props.inventoryLocations);
    var resolvedKey = location ? buildLocationSyncKey(location) : null;
    var fetchKey = resolvedKey || locationKey;
    var isRetry = locationKey === this._locationKey && location && !this.state.options.length && !this.state.loading;
    if (fetchKey === this._locationKey && !isRetry) return;

    var prevLocationKey = this._locationKey;
    this._locationKey = fetchKey;

    if (!location) {
      return;
    }

    this.fetchItemsAtLocation(location, {
      preselectAll: this.shouldPreselectAll(prevLocationKey, fetchKey, props),
    });
  }

  fetchItemsAtLocation(location, { preselectAll = false, openModal = false } = {}) {
    const { inventoryLocations } = this.props;
    const payload = buildGetItemsByLocationPayload(location, inventoryLocations);
    if (!payload) return;

    location = payload.location;
    const { components, section_id, onLocationItemsLoadingChange, isShipmentByLocation, isTransferByLocation } = this.props;
    const isLocationForm = isShipmentByLocation || isTransferByLocation;
    var sectionDefaultLabel = getSectionDefaultLabel(components, section_id);

    var locationLabel = renderLocationLabel(location);
    var requestId = ++this._fetchRequestId;

    if (isLocationForm && onLocationItemsLoadingChange) onLocationItemsLoadingChange(true);

    this.setState({
      locationLabel,
      loading: true,
      ...(openModal ? { showItemsByLocation: true } : {}),
    });

    this.props.getItemsByLocation(payload.data, (res) => {
      if (isLocationForm && onLocationItemsLoadingChange) onLocationItemsLoadingChange(false);
      if (requestId !== this._fetchRequestId) return;

      var options = formatLocationItemsResponse(res, sectionDefaultLabel);
      var nextState = { options, loading: false, locationLabel };

      if (preselectAll) {
        var preselected = buildPreselectedFromOptions(options);
        nextState.value = preselected;
        var itemsId = this.props.id;
        var quantityComponent = this.getQuantityComponent();
        this._syncedItemsKey = getLocationCacheKey(this.props);
        setTimeout(() => {
          if (requestId !== this._fetchRequestId) return;
          this.props.onChange(itemsId, preselected);
          if (quantityComponent) {
            this.props.onChange(quantityComponent.id, getTotalSelected(preselected));
          }
        }, 0);
      }
      this.setState(nextState);
      
    });
  }

  openItemsPicker = () => {
    const { components, submission, position, section_id, defaultComponent, readOnly, setFormScroll } = this.props;
    if (defaultComponent || readOnly) return;
    if (!this.shouldAllowEmbeddedView()) return;

    var location = getLocationFromSubmission(components, submission, section_id, position, {
      isTransferByLocation: this.props.isTransferByLocation,
      inventoryLocations: this.props.inventoryLocations,
    });
    if (!location) {
      simpleAlert("", "Please select a location before choosing items.", "Accept", "", "", null);
      return;
    }

    if (setFormScroll) setFormScroll(false);

    if (this.state.loading || this.state.options.length) {
      this.setState({
        showItemsByLocation: true,
        locationLabel: renderLocationLabel(location),
      });
    } else {
      this.fetchItemsAtLocation(location, {
        openModal: true,
        preselectAll: !this.getExistingValue(this.props)?.length,
      });
    }

    setTimeout(() => {
      if (setFormScroll) setFormScroll(true);
    }, 300);
  };

  onSelectedItemsChange(selected) {
    const { onChange, id } = this.props;
    this.setState({ value: selected });
    onChange(id, selected);
  }

  getQuantityComponent() {
    const { components, id, isShipmentLog, defaultLabel } = this.props;
    if (isShipmentLog) {
      if (defaultLabel == "lotcode_finished") {
        return getComponentByanotherComponentId(id, "quantityFinishedGoods", components);
      }
      if (defaultLabel == "lotcode_wip") {
        return getComponentByanotherComponentId(id, "quantityWIP", components);
      }
      return getComponentByanotherComponentId(id, "quantityIngredient", components)
        || getComponentByanotherComponentId(id, "quantityPackaging", components);
    }
    return getComponentByanotherComponentId(id, "quantity", components)
      || getComponentByanotherComponentId(id, "quantityFinishedGoods", components)
      || getComponentByanotherComponentId(id, "quantityWIP", components)
      || getComponentByanotherComponentId(id, "quantityIngredient", components);
  }

  render() {
    const { value, showItemsByLocation, options, loading, locationLabel } = this.state;
    const {
      label,
      placeholder,
      width,
      defaultComponent,
      isInventoryReceivingForm,
      isIssuable,
      isCompleted,
      openIssue,
      id,
      position,
      components,
      submission,
      defaultLabel,
      isRecipe,
      defaultYield,
      setFormScroll,
      readOnly,
      units: unitsProp,
      isShipmentByLocation,
      isTransferByLocation,
      complete_date,
    } = this.props;

    const isLocationForm = isShipmentByLocation || isTransferByLocation;

    const requiredAmount = isInventoryReceivingForm
      ? getRequiredAmount(components, id, position, isRecipe, submission, defaultLabel, defaultYield)
      : 0;
    var units = unitsProp || getComponentSubmit(submission, components, "units", id, position);
    var amount = getComponentSubmit(submission, components, "quantity", id, position);
    if (!amount) amount = getComponentSubmit(submission, components, "quantityFinishedGoods", id, position);
    if (!amount) amount = getComponentSubmit(submission, components, "quantityWIP", id, position);
    if (!amount) amount = getComponentSubmit(submission, components, "quantityIngredient", id, position);
    if (amount) amount = parseFloat(amount);

    var sectionDefaultLabel = getSectionDefaultLabel(components, this.props.section_id);
    var showAllInventoryTypes = isAllInventorySection(sectionDefaultLabel);
    var displayText = getItemsDisplayString(value, showAllInventoryTypes) || placeholder || "Select items from location";
    var minorAmount = requiredAmount > 0 && value?.length > 0
      && (parseFloat(getTotalSelected(value)) < parseFloat(requiredAmount) || parseFloat(getTotalSelected(value)) > parseFloat(requiredAmount));
    const compactShipment = isLocationForm && (Platform.OS === "android" || isMobileWeb() || isTransferByLocation);

    return (
      <View ref={this.inputRef}>
        {showItemsByLocation ? (
          <ItemsByLocationModal
            units={units}
            amount={amount}
            itemsSelected={value}
            options={options}
            loading={loading}
            locationLabel={locationLabel}
            readOnly={readOnly || defaultComponent}
            name={label || placeholder || "Items at Location"}
            onBack={(editQty, totalSelected) => {
              this._embeddedClosedUntil = Date.now() + 900;
              setTimeout(() => {
                this.setState({ showItemsByLocation: false });
              }, 100);
              if (editQty) {
                var quantityComponent = this.getQuantityComponent();
                if (quantityComponent) {
                  this.props.onChange(quantityComponent.id, totalSelected);
                }
              }
            }}
            placeholder={"Search by product or lot code"}
            onSelect={(selected) => this.onSelectedItemsChange(selected)}
            openAlert={(val) => this.setState({ showItemsByLocation: !val })}
            requiredAmount={requiredAmount}
            isShipmentByLocation={isLocationForm}
            showInventoryType={showAllInventoryTypes}
            components={components}
            setFormScroll={setFormScroll}
            complete_date={complete_date}
          />
        ) : null}
        <View style={{ ...(compactShipment ? styles.containerCompact : styles.container), paddingTop: width ? 0 : 5, flex: compactShipment ? 0 : 1 }}>
          {label ? <Text style={styles.label}>{label}</Text> : null}
          {(isIssuable && isCompleted) && (
            <TouchableOpacity
              style={{ paddingBottom: 10, paddingTop: 5 }}
              onPress={() => { if (openIssue) openIssue(); }}
            >
              <Text style={{ color: colors.primary, fontWeight: "bold" }}>Issue</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            disabled={defaultComponent || readOnly}
            style={{
              ...styles.pickerBtn,
              borderRadius: isInventoryReceivingForm ? 20 : 5,
              backgroundColor: isInventoryReceivingForm
                ? (minorAmount ? colors.quit : colors.primary)
                : "white",
              width: "100%",
              ...(compactShipment ? styles.pickerBtnCompact : null),
            }}
            onPress={this.openItemsPicker}
          >
            <Text
              numberOfLines={compactShipment ? 2 : undefined}
              style={{
              ...styles.pickerTxt,
              fontSize: compactShipment ? 12 : (isInventoryReceivingForm ? 14 : 16),
              color: isInventoryReceivingForm ? "white" : "#424242",
              fontStyle: displayText === (placeholder || "Select items from location") ? "italic" : "normal",
            }}>
              {displayText}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }
}

export default connect((state) => ({
  company_id: state.user.get("company_id"),
}), (dispatch) => ({
  getItemsByLocation: (data, callback) => dispatch(getItemsByLocation(data, callback)),
}))(ItemsByLocation);

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    paddingTop: 5,
    flex: 1,
  },
  containerCompact: {
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    width: "100%",
    alignSelf: "stretch",
  },
  label: {
    fontSize: 20,
    color: "#939598",
    fontWeight: "300",
    fontFamily: "Roboto",
  },
  pickerBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#e7e7e7",
    padding: 10,
    minHeight: 44
  },
  pickerBtnCompact: {
    minHeight: 42,
    paddingVertical: 8,
    paddingHorizontal: 6,
    borderRadius: 5,
    width: "100%",
    maxWidth: "100%",
  },
  pickerTxt: {
    fontFamily: "Roboto",
    fontSize: 16,
    textAlign: "center",
  },
  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
});
